/**
 * WaitlistPage  —  root "/" route (kept as the file name for historical reasons;
 * post-launch this is the marketing landing page that funnels to /signup).
 *
 * Section order:
 *   LandingNav              (fixed, overlays hero)
 *   HeroAnimation           (100dvh cinematic intro)
 *   HowItWorks              (3-step explainer)
 *   OccasionsSection        (full list of supported occasions)
 *   SampleCardsSection      (gallery of real card designs)
 *   WhyNotReminderSection   (honest comparison)
 *   FeaturesSection         (6-feature grid — "Why MomentSent")
 *   RealCardSection         (Photo of the real launch-day test card — proof)
 *   SocialProofBar          (one-line social proof strip)
 *   TestimonialsSection     (3 beta member quotes)
 *   WrappedTeaserSection    (Relationship Wrapped teaser)
 *   FAQSection              (frequently asked questions)
 *   PricingTeaser           (3-tier pricing preview)
 *   SignupSection           ("Start free trial" CTA — funnels to /signup)
 *   Footer
 */

import { useEffect, useState } from 'react';
import { useSearchParams }        from 'react-router-dom';
import LandingNav                 from '../components/LandingNav';
import HeroAnimation              from '../components/HeroAnimation';
import HowItWorks                 from '../components/HowItWorks';
import OccasionsSection           from '../components/OccasionsSection';
import SampleCardsSection         from '../components/SampleCardsSection';
import WhyNotReminderSection      from '../components/WhyNotReminderSection';
import FeaturesSection            from '../components/FeaturesSection';
import RealCardSection            from '../components/RealCardSection';
import SocialProofBar             from '../components/SocialProofBar';
import TestimonialsSection        from '../components/TestimonialsSection';
import WrappedTeaserSection       from '../components/WrappedTeaserSection';
import PricingTeaser              from '../components/PricingTeaser';
import FAQSection                 from '../components/FAQSection';
import SignupSection               from '../components/SignupSection';
import Footer                     from '../components/Footer';
import PageSEO                    from '../components/PageSEO';

const HOME_STRUCTURED_DATA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'MomentSent',
    url: 'https://momentsent.com',
    logo: 'https://momentsent.com/og-image.png',
    founder: {
      '@type': 'Person',
      name: 'Pete Cote',
      jobTitle: 'Founder & CEO',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@momentsent.com',
      contactType: 'customer support',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does MomentSent know when to send my card?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'For recurring occasions like birthdays and anniversaries, you set the date once and MomentSent automatically mails the card every year, timed to arrive right on time. For one-time cards, you pick the exact arrival date when you schedule it.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are these real physical cards, or digital eCards?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Real, physical greeting cards. MomentSent prints your message on quality cardstock and mails it first-class so a tangible card arrives in your recipient\'s mailbox. It is not an eCard service or a digital experience page.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does pricing work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Each plan includes a yearly allowance of folded cards: 12 on Thoughtful, 24 on Connected, and 48 on Premium, with printing and first-class postage included. If you send more than your allowance, extra cards are a flat $5.99 each. Your plan price also sets how many people you can send to (up to 5, up to 20, or unlimited).',
        },
      },
      {
        '@type': 'Question',
        name: 'What does the card look like? Is it a generic template?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You write the message in your own words and it is printed on a real greeting card. It is completely personal because it comes from you. You can preview how it looks before it goes to print.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I see the message before it is mailed?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, always. For recurring cards you get a notification 14 days before the send date with the draft message. You can approve it, edit it, or rewrite it completely. We never mail anything you have not had a chance to review.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need a subscription to use MomentSent?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can start with a 14-day free trial with no credit card required. After that, plans start at $7.99 per month for up to 5 contacts. The Connected plan at $15.99 per month covers up to 20 contacts, and the Premium plan at $27.99 per month covers unlimited contacts. Each plan includes a yearly allowance of real folded cards (12, 24, or 48); any extra cards are a flat $5.99 each.',
        },
      },
      {
        '@type': 'Question',
        name: 'What countries do you ship to?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MomentSent mails to over 100 countries through professional print and fulfillment partners. US addresses ship standard first-class. International delivery typically takes 7 to 14 business days.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is MomentSent only for birthdays?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Not at all. Birthdays are just the start. MomentSent also sends cards for anniversaries, Christmas, Valentine\'s Day, Mother\'s Day, Father\'s Day, graduations, thinking of you notes, and any custom occasion you name. If there is a date that matters, we remember it.',
        },
      },
    ],
  },
];

export default function WaitlistPage() {
  const [heroComplete, setHeroComplete] = useState(false);
  const [searchParams] = useSearchParams();

  // Persist ?ref= through navigation to /signup (sessionStorage as relay)
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      sessionStorage.setItem('referral_code', ref.toUpperCase());
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-cream">
      <PageSEO
        title="MomentSent | Automatic Birthday Card Mailing Service"
        description="MomentSent mails real physical greeting cards for birthdays, anniversaries, and every occasion that matters. Set it once and never miss a moment. Start your 14-day free trial today."
        canonical="https://momentsent.com/"
        structuredData={HOME_STRUCTURED_DATA}
      />

      {/* Fixed nav — always visible, transparent over hero, opaque when scrolled */}
      <LandingNav />

      {/* ── Hero (100vh — cinematic animation) ──────────────────────────────── */}
      <section className="relative w-full" style={{ height: '100dvh', minHeight: 560 }}>
        <HeroAnimation
          onComplete={() => setHeroComplete(true)}
          onSkip={()    => setHeroComplete(true)}
        />
      </section>

      {/* ── Everything below the fold (fades in once hero completes) ──────── */}
      <div
        style={{
          opacity:       heroComplete ? 1 : 0,
          transition:    'opacity 0.6s ease-in-out',
          // Prevent interaction until hero is done (avoids tab index bleed)
          pointerEvents: heroComplete ? 'auto' : 'none',
        }}
      >
        {/* ── Brand wordmark strip ──────────────────────────────────────── */}
        <div className="bg-cream py-10 flex justify-center">
          <img
            src="/logo-wordmark.png"
            alt="MomentSent"
            className="h-24 w-auto object-contain opacity-90"
          />
        </div>

        {/* ── Founder pull quote strip ──────────────────────────────────── */}
        <div className="bg-navy py-12 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <svg className="w-8 h-8 text-sage/50 mx-auto mb-5" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
              <path d="M10 8C6.686 8 4 10.686 4 14v10h10V14H7.5C7.5 11.514 8.962 9.5 10 9.5V8zm14 0c-3.314 0-6 2.686-6 6v10h10V14h-6.5c0-2.486 1.462-4.5 2.5-4.5V8z"/>
            </svg>
            <p className="font-heading text-xl md:text-2xl text-cream italic leading-relaxed">
              I'd call home after a deployment and find out a birthday had passed a week ago.
              That guilt doesn't go away easily. I needed a system, so I built one.
            </p>
            <p className="mt-5 font-body text-sage text-sm font-semibold tracking-widest uppercase">
             Pete Cote, Founder &amp; CEO
            </p>
          </div>
        </div>

        <HowItWorks              />
        <OccasionsSection        />
        <SampleCardsSection      />
        <WhyNotReminderSection   />
        <FeaturesSection         />
        <RealCardSection         />
        <SocialProofBar          />
        <TestimonialsSection     />
        <WrappedTeaserSection    />
        <FAQSection              />
        <PricingTeaser           />
        <SignupSection           />
        <Footer                  />
      </div>

    </div>
  );
}
